import React, { useEffect } from 'react';

import { Box, Button, Checkbox, Chip, CircularProgress, Grid, Link, Modal, Radio, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { green, red } from '@mui/material/colors';

import { modalStyle } from '../../../components/modals/modalStyle';
import { InvoiceListItem, InvoicesPaymentCommand } from '../../../apis/invoiceApi';
import { formatDate } from '../../../utils/formatUtils';
import { usePaymentMethodStore, useSessionStore } from '../../../stores';
import PaymentMethodTypeIcon from '../../../components/paymentMethods/PaymentMethodTypeIcon';
import { CardFunding, formatExpBank, isExpired, PaymentMethodType } from '../../../utils/paymentMethodUtils';
import { PaymentMethodListItem } from '../../../apis/paymentMethodApi';
import { payInvoices } from '../../../apis/paymentApi';
import { getPaymentSettings, PaymentSettings } from '../../../apis/paymentSettingApi';

const SectionHeader = styled(Typography)`
  padding-left: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
  background-color: ${(props) => props.theme.palette.info.light};
  color: ${(props) => props.theme.palette.info.contrastText};
`;

interface PayInvoicesModalProps {
  isOpen: boolean;
  isLoading: boolean;
  allowSurcharging: boolean;
  invoices: InvoiceListItem[];
  onCancel: () => void;
  onPaymentsSubmitted: (command: InvoicesPaymentCommand) => void;
  onAddPaymentMethodClick: () => void;
}

interface PayInvoiceItem extends InvoiceListItem {
  isSelected: boolean;
}

interface PaymentMethodItem extends PaymentMethodListItem {
  isSelected: boolean;
}

export default function PayInvoicesModal({
  isOpen,
  isLoading,
  invoices,
  onPaymentsSubmitted,
  onAddPaymentMethodClick,
  onCancel,
}: PayInvoicesModalProps) {
  const companyName = useSessionStore.use.companyName();

  const [outstandingInvoices, setOutstandingInvoices] = React.useState<PayInvoiceItem[]>([]);
  const [invoiceTotal, setInvoiceTotal] = React.useState(0);

  const allPaymentMethods = usePaymentMethodStore.use.paymentMethods();
  const retrievePaymentMethods = usePaymentMethodStore.use.retrievePaymentMethods();
  const newPaymentMethodId = usePaymentMethodStore.use.newPaymentMethodId();

  const [addedPaymentMethodId, setAddedPaymentMethodId] = React.useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethodItem[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethodItem | undefined | null>();

  const [canPay, setCanPay] = React.useState(false);

  const paymentSettings = useSessionStore.use.paymentSettings();
  const setPaymentSettings = useSessionStore.use.setPaymentSettings();

  useEffect(() => {
    if (!paymentSettings && invoices?.length) {
      getPaymentSettings().then((settings: PaymentSettings) => {
        setPaymentSettings(settings);
      });
    }
  }, [invoices]);

  useEffect(() => {
    if (isOpen) {
      retrievePaymentMethods(true);
      const outstandingInvoices = invoices
        .filter((i) => i.amountDue > 0)
        .sort((a, b) => a.invoiceNumber - b.invoiceNumber)
        .map((i) => ({ ...i, isSelected: true }));
      setOutstandingInvoices([...outstandingInvoices]);
    }
  }, [isOpen]);

  useEffect(() => {
    const isCreditCard = selectedPaymentMethod?.paymentType === PaymentMethodType.CARD && selectedPaymentMethod?.cardFunding === CardFunding.Credit;
    const useSurcharge = paymentSettings?.surchargingEnabled && isCreditCard;
    const invoiceTotal = outstandingInvoices
      .filter((x) => x.isSelected)
      .reduce((acc, i) => acc + (useSurcharge ? (i.totalWithSurcharge ?? i.amountDue) : i.amountDue), 0);
    setInvoiceTotal(invoiceTotal);
  }, [outstandingInvoices, selectedPaymentMethod]);

  useEffect(() => {
    if (newPaymentMethodId) {
      setAddedPaymentMethodId(newPaymentMethodId);
    }
  }, [newPaymentMethodId]);

  useEffect(() => {
    if (isOpen) {
      let selectedPaymentMethod = null;
      const paymentMethodItems = allPaymentMethods.map((i) => ({ ...i, isSelected: false }));
      if (addedPaymentMethodId) {
        selectedPaymentMethod = paymentMethodItems.find((i) => i.id === addedPaymentMethodId);
      }
      if (!selectedPaymentMethod) {
        selectedPaymentMethod = paymentMethodItems.find((i) => !isExpired(i));
      }

      if (selectedPaymentMethod) {
        selectedPaymentMethod.isSelected = true;
      }

      setPaymentMethods(paymentMethodItems);
      setSelectedPaymentMethod(selectedPaymentMethod);
    }
  }, [allPaymentMethods, isOpen]);

  useEffect(() => {
    const canPay = !!selectedPaymentMethod && invoiceTotal > 0;
    setCanPay(canPay);
  }, [selectedPaymentMethod, invoiceTotal]);

  const handleCancelClick = () => {
    onCancel();
  };

  const handlePerformPaymentClick = async () => {
    const invoiceIds = outstandingInvoices.filter((x) => x.isSelected).map((i) => i.id);

    if (invoiceIds.length > 0 && selectedPaymentMethod?.id) {
      const paymentRequest = { invoiceIds, paymentMethodId: selectedPaymentMethod.id, paymentMethodType: selectedPaymentMethod.paymentType };

      await payInvoices(paymentRequest);
      onPaymentsSubmitted(paymentRequest);
    }
  };

  const handleInvoiceToggle = (invoiceId: string) => {
    const invoice = outstandingInvoices.find((i) => i.id === invoiceId);
    if (invoice) {
      invoice.isSelected = !invoice.isSelected;
      setOutstandingInvoices([...outstandingInvoices]);
    }
  };

  const handleAddPaymentMethodClick = () => {
    onAddPaymentMethodClick();
  };

  const handlePaymentMethodClick = (paymentMethodId: string) => {
    for (const paymentMethod of paymentMethods) {
      if (paymentMethod.id === paymentMethodId) {
        setSelectedPaymentMethod(paymentMethod);
        paymentMethod.isSelected = true;
      } else {
        paymentMethod.isSelected = false;
      }
    }
    setPaymentMethods([...paymentMethods]);
  };

  return (
    <Modal open={isOpen} onClose={onCancel}>
      <Box sx={{ ...modalStyle }}>
        <Typography mt={{ xs: 0, sm: 1 }} variant="h4">
          Pay Now
        </Typography>
        <Typography mt={2} mb={3} variant="subtitle1" sx={{ color: (theme) => theme.palette.action.active }}>
          Pay ${invoiceTotal.toFixed(2)} for {outstandingInvoices.filter((x) => x.isSelected).length} unpaid invoices.
        </Typography>
        <SectionHeader variant="h6" mb={2}>
          Choose Invoices
        </SectionHeader>
        <Grid container spacing={0} pl={2} border={1} borderColor={(theme) => theme.palette.divider} overflow="auto" sx={{ maxHeight: 163 }}>
          {outstandingInvoices.map((invoice) => (
            <React.Fragment key={invoice.id}>
              <Grid item xs={1}>
                <Checkbox checked={invoice.isSelected} onChange={() => handleInvoiceToggle(invoice.id)} sx={{ paddingX: 0 }} />
              </Grid>
              <Grid item xs={5}>
                <Typography pt="10px" variant="subtitle2">
                  Invoice #{invoice.invoiceNumber}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography pt="10px" variant="subtitle2">
                  ${invoice.amountDue.toFixed(2)}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <SectionHeader variant="h6" mt={4} mb={2}>
          Choose a Payment Method
        </SectionHeader>
        <Grid container spacing={0} pl={2} border={1} borderColor={(theme) => theme.palette.divider} overflow="auto" sx={{ maxHeight: 163 }}>
          {paymentMethods.map((paymentMethod) => (
            <React.Fragment key={paymentMethod.id}>
              <Grid item xs={1}>
                <Radio
                  name="payment-method-for-invoices"
                  checkedIcon={<CheckCircleIcon />}
                  sx={{ paddingX: 0 }}
                  checked={paymentMethod.isSelected}
                  onChange={() => handlePaymentMethodClick(paymentMethod.id)}
                />
              </Grid>
              <Grid item xs={1} pt="6px" pl={1} alignContent="center">
                <PaymentMethodTypeIcon paymentMethod={paymentMethod} isSmall={true} />
              </Grid>
              <Grid item xs={2} pl={5}>
                <Typography pt="10px" variant="subtitle2">
                  *{paymentMethod.last4}
                </Typography>
              </Grid>
              <Grid item xs={5} pl={5}>
                <Typography pt="10px" variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatExpBank(paymentMethod, 'exp - ')}
                </Typography>
              </Grid>
              <Grid item xs={3} alignContent="center" textAlign="right" pr={3}>
                {isExpired(paymentMethod) && (
                  <Chip
                    label="Expired"
                    size="small"
                    sx={{
                      backgroundColor: red[100],
                      color: red[800],
                      '& .MuiChip-label': {
                        overflow: 'visible',
                      },
                    }}
                  />
                )}
                {!isExpired(paymentMethod) && paymentMethod.autopay && (
                  <Chip
                    label="AutoPay"
                    size="small"
                    sx={{
                      backgroundColor: green[100],
                      color: green[800],
                      '& .MuiChip-label': {
                        overflow: 'visible',
                      },
                    }}
                  />
                )}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        <Box>
          <Box mt={2} mb={3}>
            <Typography variant="caption" sx={{ color: (theme) => theme.palette.action.active }}>
              {paymentSettings?.surchargingEnabled && (
                <>
                  If paying by credit card, an additional credit surcharge of no more than 3% may be imposed.
                  <br />
                </>
              )}
              By selecting <b>Pay</b>, I accept the{' '}
              <Link
                sx={{ color: (theme) => theme.palette.action.active, textDecoration: 'underline' }}
                href="https://www.getskimmer.com/home/terms"
                target="_blank"
              >
                Terms of Service
              </Link>{' '}
              and have read and acknowledge the{' '}
              <Link
                sx={{ color: (theme) => theme.palette.action.active, textDecoration: 'underline' }}
                href="https://www.getskimmer.com/home/privacy"
                target="_blank"
              >
                Privacy Statement
              </Link>
              . I also allow {companyName} to charge ${invoiceTotal.toFixed(2)} to my card or account on {formatDate(new Date())}.
            </Typography>
          </Box>
          <Grid container>
            <Grid item xs={5} pl={0}>
              <Button
                disabled={isLoading}
                onClick={() => {
                  handleAddPaymentMethodClick();
                }}
              >
                Add Payment Method
              </Button>
            </Grid>
            <Grid item xs={7} style={{ textAlign: 'right' }}>
              <Button
                disabled={isLoading}
                sx={{
                  marginRight: 3,
                }}
                onClick={() => {
                  handleCancelClick();
                }}
              >
                Cancel
              </Button>
              <Box display="inline-block" title={canPay ? '' : 'Select invoices to pay'}>
                <Button disabled={isLoading || !canPay} color="success" variant="contained" onClick={handlePerformPaymentClick}>
                  {isLoading && (
                    <CircularProgress
                      size={14}
                      sx={{
                        color: (theme) => theme.palette.secondary.main,
                        marginRight: 1,
                      }}
                    />
                  )}
                  Pay ${invoiceTotal.toFixed(2)}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
}
